/**
 * Browser Detection Utility
 * Detects browser type, mobile device, and printer capabilities
 */

export interface BrowserInfo {
  name: string;
  version: string;
  isMobile: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isSunmiDevice: boolean;
  supportsNativePrint: boolean;
}

export class BrowserDetector {

  /**
   * Detect current browser and device information
   */
  static detect(): BrowserInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || '';

    // Browser detection
    let browserName = 'unknown';
    let browserVersion = '';

    if (userAgent.includes('firefox')) {
      browserName = 'firefox';
      const match = userAgent.match(/firefox\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      browserName = 'chrome';
      const match = userAgent.match(/chrome\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      browserName = 'safari';
      const match = userAgent.match(/version\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('edg')) {
      browserName = 'edge';
      const match = userAgent.match(/edg\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    }

    // Device detection
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);

    // Sunmi device detection
    const isSunmiDevice = userAgent.includes('sunmi') ||
                          userAgent.includes('ssunni') ||
                          this.checkSunmiPrinterAPI();

    // Native print support check
    const supportsNativePrint = this.checkSunmiPrinterAPI();

    return {
      name: browserName,
      version: browserVersion,
      isMobile,
      isAndroid,
      isIOS,
      isSunmiDevice,
      supportsNativePrint
    };
  }

  /**
   * Check if Sunmi Printer API is available
   */
  static checkSunmiPrinterAPI(): boolean {
    return typeof window !== 'undefined' &&
           !!(window as any).SunmiPrinter;
  }

  /**
   * Check if running in WebView
   */
  static isWebView(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('wv') ||
           userAgent.includes('webview') ||
           !!(window as any).Android ||
           !!(window as any).webkit?.messageHandlers;
  }

  /**
   * Get printer method recommendation based on browser and device
   */
  static getRecommendedPrintMethod(): 'sunmi' | 'browser' | 'api' {
    const info = this.detect();

    // If Sunmi API is available, use it
    if (info.supportsNativePrint) {
      return 'sunmi';
    }

    // If on Sunmi device but no API, suggest using print service API
    if (info.isSunmiDevice || info.isAndroid) {
      return 'api';
    }

    // Fallback to browser print
    return 'browser';
  }

  /**
   * Log browser and device information
   */
  static logInfo(): void {
    const info = this.detect();
    console.log('=== Browser & Device Info ===');
    console.log('Browser:', info.name, info.version);
    console.log('Mobile:', info.isMobile);
    console.log('Android:', info.isAndroid);
    console.log('iOS:', info.isIOS);
    console.log('Sunmi Device:', info.isSunmiDevice);
    console.log('Native Print Support:', info.supportsNativePrint);
    console.log('Recommended Print Method:', this.getRecommendedPrintMethod());
    console.log('Is WebView:', this.isWebView());
    console.log('User Agent:', navigator.userAgent);
    console.log('=============================');
  }
}
